"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

const CHALDEAN_MAP: Record<string, number> = {
  A: 1,
  I: 1,
  J: 1,
  Q: 1,
  Y: 1,
  B: 2,
  K: 2,
  R: 2,
  C: 3,
  G: 3,
  L: 3,
  S: 3,
  D: 4,
  M: 4,
  T: 4,
  E: 5,
  H: 5,
  N: 5,
  X: 5,
  U: 6,
  V: 6,
  W: 6,
  O: 7,
  Z: 7,
  F: 8,
  P: 8,
};

const PLANET_MAP: Record<number, { name: string; symbol: string; trait: string }> = {
  1: { name: "Sun (Surya)", symbol: "☀️", trait: "Leadership, originality, authority & ambition" },
  2: {
    name: "Moon (Chandra)",
    symbol: "🌙",
    trait: "Intuition, empathy, adaptability & creativity",
  },
  3: {
    name: "Jupiter (Guru)",
    symbol: "🟡",
    trait: "Wisdom, expansion, optimism & higher counsel",
  },
  4: {
    name: "Rahu (Uranus)",
    symbol: "⚡",
    trait: "Innovation, unconventional intellect & breakthroughs",
  },
  5: {
    name: "Mercury (Budh)",
    symbol: "🟢",
    trait: "Fast communication, business acumen & versatility",
  },
  6: {
    name: "Venus (Shukra)",
    symbol: "💎",
    trait: "Magnetism, luxury, aesthetic harmony & warmth",
  },
  7: {
    name: "Ketu (Neptune)",
    symbol: "🌊",
    trait: "Spiritual depth, philosophical insight & research",
  },
  8: {
    name: "Saturn (Shani)",
    symbol: "🪐",
    trait: "Material mastery, perseverance & karmic duty",
  },
  9: {
    name: "Mars (Mangal)",
    symbol: "🔴",
    trait: "Courage, dynamic drive, protection & vitality",
  },
};

const COMPOUND_DESCRIPTIONS: Record<
  number,
  { title: string; nature: "Fortunate" | "Moderate" | "Challenging"; desc: string }
> = {
  10: {
    title: "Wheel of Fortune",
    nature: "Fortunate",
    desc: "Honor, faith, and self-confidence. Rises to executive leadership.",
  },
  14: {
    title: "Movement & Media",
    nature: "Fortunate",
    desc: "Magnetic communication, commercial agility, and global contacts.",
  },
  15: {
    title: "Magician & Charm",
    nature: "Fortunate",
    desc: "Immense artistic charm, luxury, and persuasive speaking power.",
  },
  19: {
    title: "Prince of Heaven",
    nature: "Fortunate",
    desc: "Great victory, happiness, success in all undertakings.",
  },
  23: {
    title: "Royal Star of the Lion",
    nature: "Fortunate",
    desc: "One of the most potent name numbers. Guarantees help from high-ranking superiors.",
  },
  24: {
    title: "Love & Gain",
    nature: "Fortunate",
    desc: "Harmonious partnerships, financial security, and affectionate family life.",
  },
  27: {
    title: "The Sceptre",
    nature: "Fortunate",
    desc: "Intellectual authority, executive command, and respect from subordinates.",
  },
  32: {
    title: "Star of Diplomacy",
    nature: "Fortunate",
    desc: "Wisdom in large organizations, political acclaim, and high commercial skill.",
  },
  37: {
    title: "Crown of Friendship",
    nature: "Fortunate",
    desc: "True friendships, happy partnerships, and effortless material blessings.",
  },
  41: {
    title: "Strategic Acclaim",
    nature: "Fortunate",
    desc: "Sharp business vision, successful leadership in competitive arenas.",
  },
};

function calculateChaldean(name: string): {
  compound: number;
  single: number;
  breakdown: { char: string; val: number }[];
} {
  const clean = name.toUpperCase().replace(/[^A-Z]/g, "");
  let compound = 0;
  const breakdown: { char: string; val: number }[] = [];

  for (const char of clean) {
    const val = CHALDEAN_MAP[char] || 0;
    compound += val;
    breakdown.push({ char, val });
  }

  let single = compound;
  while (single > 9) {
    single = String(single)
      .split("")
      .reduce((acc, digit) => acc + Number(digit), 0);
  }

  return { compound, single: single || 1, breakdown };
}

export default function NameNumerologyStudio() {
  const [inputName, setInputName] = useState<string>("Ishvaram");

  const result = useMemo(() => calculateChaldean(inputName), [inputName]);
  const planet = useMemo(() => PLANET_MAP[result.single] || PLANET_MAP[1], [result.single]);
  const compoundInfo = useMemo(() => {
    return (
      COMPOUND_DESCRIPTIONS[result.compound] || {
        title: `Vibration ${result.compound}`,
        nature:
          result.single === 1 || result.single === 3 || result.single === 5 || result.single === 6
            ? "Fortunate"
            : "Moderate",
        desc: `Carries the baseline core energy of Root Number ${result.single}, governed by ${planet.name}.`,
      }
    );
  }, [result, planet]);

  const handleConsult = () => {
    const prompt = `Please analyze the Chaldean numerology of the name "${inputName}". Total compound is ${result.compound}, single root is ${result.single} (${planet.name}). Is this name spelling harmonious with my date of birth?`;
    try {
      safeSessionSet("seo_entry_intent", "numerology_lookup");
      safeSessionSet("seo_entry_path", "/numerology");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <NameInputSection inputName={inputName} onChange={setInputName} />
      <NameBreakdownView breakdown={result.breakdown} />
      <NameResultSummary
        result={result}
        planet={planet}
        compoundInfo={compoundInfo}
        onConsult={handleConsult}
      />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Vedic Ank Jyotish
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        Chaldean Name Numerology Calculator (नाम अंक ज्योतिष)
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Enter any name to calculate its Chaldean compound number, single root vibration, ruling
        Graha, and harmonious spelling resonance.
      </p>
    </div>
  );
}

function NameInputSection({
  inputName,
  onChange,
}: {
  inputName: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="mb-6">
      <label
        htmlFor="name-input"
        className="block text-caption font-bold uppercase tracking-wider text-text-muted mb-2"
      >
        Enter Name or Brand (English Alphabet)
      </label>
      <input
        id="name-input"
        type="text"
        value={inputName}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Vikram Sharma, Aarav, Ishvaram"
        className="w-full rounded-xl border border-border-default bg-surface-raised px-4 py-3 text-body font-semibold text-text-primary focus:border-border-interactive focus:outline-none focus:ring-2 focus:ring-intent-brand"
      />
    </div>
  );
}

function NameBreakdownView({ breakdown }: { breakdown: { char: string; val: number }[] }) {
  if (!breakdown.length) return null;
  return (
    <div className="mb-6">
      <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-2">
        Letter-by-Letter Chaldean Value Breakdown
      </p>
      <div className="flex flex-wrap gap-1.5 overflow-x-auto pb-1">
        {breakdown.map((item, idx) => (
          <div
            key={idx}
            className="flex flex-col items-center justify-center p-2 min-w-[36px] rounded-lg bg-surface-raised border border-border-default"
          >
            <span className="text-body-small font-bold text-text-primary">{item.char}</span>
            <span className="text-caption font-semibold text-intent-brand">{item.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function NameResultSummary({
  result,
  planet,
  compoundInfo,
  onConsult,
}: {
  result: { compound: number; single: number };
  planet: { name: string; symbol: string; trait: string };
  compoundInfo: { title: string; nature: "Fortunate" | "Moderate" | "Challenging"; desc: string };
  onConsult: () => void;
}) {
  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/numerology"),
    "/numerology",
    "en"
  );

  const badgeColor =
    compoundInfo.nature === "Fortunate"
      ? "bg-success/10 text-success"
      : compoundInfo.nature === "Moderate"
        ? "bg-intent-brand/10 text-intent-brand"
        : "bg-warning/10 text-warning";

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              Compound {result.compound} &rarr; Single Root {result.single}
            </h3>
            <span className={`px-3 py-0.5 rounded-full text-caption font-bold ${badgeColor}`}>
              {compoundInfo.nature}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Arch: <strong className="text-text-primary">{compoundInfo.title}</strong> &middot;
            Governed by <span className="text-text-primary font-semibold">{planet.name}</span>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Check Name vs Birth Date ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            Compound Vibration Meaning
          </p>
          <p className="text-body-small text-text-primary leading-relaxed">{compoundInfo.desc}</p>
        </div>

        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            Root Planetary Resonance
          </p>
          <p className="text-body-small text-text-primary leading-relaxed">
            {planet.symbol} {planet.trait}
          </p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Want to align your name spelling with your Mulank and Bhagyank numbers?
        </span>
        <Link
          href="/numerology/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Calculate Mulank &amp; Bhagyank First →
        </Link>
      </div>
    </div>
  );
}
